import React, { useState, useRef } from 'react';
import { Input } from '@/components/ui/Input/Input';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { Button } from '@/components/ui/Button/Button';
import { feedbackService, type Attachment } from '@/services/feedbackService';
import { useNotificationStore } from '@/stores/notificationStore';

interface FeatureRequestFormProps {
  onSuccess: () => void;
}

export function FeatureRequestForm({ onSuccess }: FeatureRequestFormProps) {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const notify = useNotificationStore();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;

    setIsSubmitting(true);
    try {
      const ticket = await feedbackService.createTicket({
        type: 'feature',
        subject: subject || description.slice(0, 100),
        body: description,
        metadata: {
          pageUrl: window.location.href,
          timestamp: new Date().toISOString(),
        },
      });

      if (files.length > 0) {
        const attachments: Attachment[] = [];
        for (const file of files) {
          try {
            const attachment = await feedbackService.uploadAttachment(ticket.id, file);
            attachments.push(attachment);
          } catch (err) {
            console.error('Failed to upload attachment:', err);
          }
        }
        if (attachments.length > 0) {
          await feedbackService.sendMessage(ticket.id, 'Attachments:', attachments);
        }
      }

      notify.success('Feature request submitted! We appreciate your input.');
      onSuccess();
    } catch (err: any) {
      notify.error(err.message || 'Failed to submit feature request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Feature title"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Brief title for your feature idea"
      />

      <Textarea
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Describe the feature you'd like to see. How would it work? What problem would it solve for you?"
        rows={5}
        required
      />

      {/* File Attachment */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          Attachments (optional)
        </label>
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (e.dataTransfer.files) setFiles((prev) => [...prev, ...Array.from(e.dataTransfer.files)]);
          }}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-input rounded-md p-3 text-center cursor-pointer hover:border-primary-400 hover:bg-primary-50/50 transition-colors"
        >
          <p className="text-sm text-muted-foreground">
            Drop files or <span className="text-primary-600 font-medium">browse</span>
          </p>
        </div>
        <input ref={fileInputRef} type="file" multiple onChange={handleFileChange} className="hidden" />

        {files.length > 0 && (
          <div className="mt-2 space-y-1">
            {files.map((file, i) => (
              <div key={i} className="flex items-center justify-between bg-muted rounded-md px-3 py-1.5 text-sm">
                <span className="truncate text-foreground">{file.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFile(i)}
                  className="ml-2 p-1 h-auto text-muted-foreground hover:text-error-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end pt-2">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={isSubmitting || !description.trim()}
          isLoading={isSubmitting}
        >
          Submit Feature Request
        </Button>
      </div>
    </form>
  );
}
